import type { ReactNode } from "react";
import {
  createViewMode,
  type ViewMode,
  type ViewModeContextValue,
} from "@/hooks/create-view-mode";

export type RecordsViewMode = ViewMode;

const { Provider, useViewMode } = createViewMode({
  storageKey: "otus:records-view-mode",
  providerName: "RecordsViewModeProvider",
  hookName: "useRecordsViewMode",
});

/**
 * Single source of truth for the photos page's grid/list preference, shared
 * between the toggle (in the project header — [[project-header.spec]]) and the
 * content area ([[records-view.spec]]), which renders [[photo-grid.spec]] or
 * [[photo-list.spec]] accordingly. Lives at the project-layout level so
 * toggling in the header re-renders the view.
 *
 * Kept separate from the plans page's preference on purpose: the two pages show
 * different things, and someone who wants plans as cards may still want photos
 * as a grid. The choice is persisted globally in `localStorage` under
 * `otus:records-view-mode` (default `"grid"`).
 */
export function RecordsViewModeProvider({ children }: { children: ReactNode }) {
  return <Provider>{children}</Provider>;
}

/**
 * Grid/list view preference for the photos page. Returns a
 * `[viewMode, setViewMode]` tuple. Must be used within a
 * `RecordsViewModeProvider` (mounted at the project layout). See
 * [[records-view.spec]] US-004.
 */
export function useRecordsViewMode(): ViewModeContextValue {
  return useViewMode();
}
